import { usePackageSummary } from '@/hooks/usePackageSummary';
import { MasterFileRow } from './MasterFileRow';

interface PackageSummaryViewProps {
  reportId: string;
}

/**
 * Main container for package summary view
 */
export const PackageSummaryView = ({ reportId }: PackageSummaryViewProps) => {
  const { summary, isLoading, error } = usePackageSummary(reportId);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Loading package summary...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <div className="text-red-600 font-semibold mb-2">Error Loading Summary</div>
        <p className="text-red-700 text-sm">{error}</p>
      </div>
    );
  }

  // No master files found
  if (!summary || summary.master_files.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <p className="text-gray-600">No master files detected in this package.</p>
      </div>
    );
  }

  // Render master files
  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600 mb-4">
        Found {summary.master_files.length} master file{summary.master_files.length !== 1 ? 's' : ''}
      </div>

      {summary.master_files.map((masterFile, index) => (
        <MasterFileRow key={`${masterFile.file_path}-${index}`} masterFile={masterFile} />
      ))}
    </div>
  );
};
